import { Formik, FormikProps } from "formik";
import { useCallback, useMemo, useState } from "react";
import { Button, Col, Form, FormControl, FormGroup, FormLabel, Modal, Row } from "react-bootstrap";
import { createPatch } from "rfc6902";
import * as yup from "yup";
import { FormikInput, FormikSelect } from "../../component-library";
import { useToasts } from "../../contexts";
import { SelectOption, Tenant } from "../../models";
import { useAppSettingsQuery, useTenantQuery } from "../../queries";

const CustomerFormSchema = yup.object({
    name: yup.string().max(50, "Must be 50 characters or less").required("Required"),
    parentTenantId: yup.number().min(1, "Required").required("Required"),
});

type CustomerFormModel = yup.InferType<typeof CustomerFormSchema>;

interface CustomerFormProps {
    onClose: VoidFunction;
    customer?: Tenant;
}

export const CustomerForm = ({ onClose, customer }: CustomerFormProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { errorToast } = useToasts();
    const {
        myAppSettingsQuery: { data: appSettings },
    } = useAppSettingsQuery();
    const {
        listQuery: { data: tenants },
        createMutation: { mutateAsync: createTenantAsync },
        updateMutation: { mutateAsync: updateTenantAsync },
    } = useTenantQuery({ id: customer?.id });

    const initialValues = useMemo<CustomerFormModel>(
        () => ({
            name: customer?.name ?? "",
            parentTenantId: customer?.parentTenantId ?? 0,
        }),
        [customer]
    );

    const tenantOptions = useMemo<SelectOption[]>(() => {
        if (!tenants) return [];
        return tenants
            .filter((t) => !t.parentTenantId)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((t) => ({ value: t.id, label: t.name }));
    }, [tenants]);

    const parentTenant = useMemo<Tenant | undefined>(() => {
        if (!customer || !tenants) return;
        return tenants.find((t) => t.id === customer.parentTenantId);
    }, [customer, tenants]);

    const handleSubmit = useCallback(
        async (values: CustomerFormModel) => {
            setIsSubmitting(true);
            let success = false;
            try {
                if (!customer) {
                    await createTenantAsync({
                        ...values,
                        isActive: true,
                    });
                    success = true;
                } else {
                    await updateTenantAsync(createPatch(customer, { ...customer, ...values }));
                    success = true;
                }
            } catch (error) {
                errorToast(error);
            }
            if (success) {
                onClose();
            } else {
                setIsSubmitting(false);
            }
        },
        [createTenantAsync, updateTenantAsync, customer, errorToast, onClose]
    );

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={CustomerFormSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {(props: FormikProps<CustomerFormModel>) => (
                <Modal show backdrop="static" keyboard={false} size="lg">
                    <Modal.Header>
                        {!customer ? `Add New ${appSettings?.childTenantName}` : `Edit ${appSettings?.childTenantName}`}
                    </Modal.Header>
                    <Modal.Body>
                        <Form id="customerForm">
                            <Row>
                                {!customer && (
                                    <Col sm={4}>
                                        <FormikSelect
                                            name="parentTenantId"
                                            label={appSettings?.parentTenantName}
                                            placeholder={`Select a ${appSettings?.parentTenantName}...`}
                                            options={tenantOptions}
                                            disabled={isSubmitting}
                                        />
                                    </Col>
                                )}
                                <Col sm={4}>
                                    <FormikInput name="name" label="Name" disabled={isSubmitting} />
                                </Col>
                                {!!parentTenant && (
                                    <Col sm={4}>
                                        <FormGroup>
                                            <FormLabel>{appSettings?.parentTenantName}</FormLabel>
                                            <FormControl value={parentTenant.name} disabled={true} />
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button onClick={props.submitForm} className="ms-2" disabled={isSubmitting || !props.dirty}>
                            Submit
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Formik>
    );
};
